/* eslint-disable no-param-reassign */
import React from 'react';
import PropTypes from 'prop-types';
import numeral from 'numeral';

import { makeStyles, TableCell, TableRow, TableFooter, TableBody } from '@material-ui/core';

import useNavigate from 'hooks/useNavigate';
import Label from 'components/shared/Label/Label';
import { groupBy } from 'utils/array-helpers';
import Zoom from '@material-ui/core/Zoom';
import useSorting from '../../common/hooks';
import { getCellFormat } from '../utils';
import TypographyWithTooltip from '../../../../shared/TypographyWithTooltip';

const useStyles = makeStyles(theme => ({
  row: {
    textDecoration: 'none',
    '& td': {
      fontSize: theme.customStyles.tableRow.fontSize,
      padding: theme.customStyles.tableRow.padding,
      '&:not(last-child)': {
        borderLeft: `1px solid ${theme.palette.grey.medium}`
      }
    }
  },

  linkRow: {
    textDecoration: 'none',
    cursor: 'pointer',
    '&:hover': {
      boxShadow: theme.palette.boxShadow.default,
      backgroundColor: `${theme.palette.primary.main}40`
    },
    '& td': {
      fontSize: theme.customStyles.tableRow.fontSize,
      padding: '1px',
      textAlign: theme.customStyles.tdRightAlign.textAlign,
      '&:nth-child(2)': {
        maxWidth: theme.customStyles.nthChild.maxWidth,
        overflow: theme.customStyles.cuttingTextCompactMode.overflow,
        whiteSpace: theme.customStyles.cuttingTextCompactMode.whiteSpace,
        textOverflow: theme.customStyles.cuttingTextCompactMode.textOverflow,
        textAlign: theme.customStyles.tdLeftAlign.textAlign
      },
      '&:first-child': {
        textAlign: theme.customStyles.tdLeftAlign.textAlign
      },
      '&:not(last-child)': {
        borderLeft: `1px solid ${theme.palette.grey.medium}`
      }
    }
  },
  groupRow: {
    backgroundColor: theme.palette.grey.light,
    '& td': {
      fontSize: theme.customStyles.groupedBodyRow.fontSize,
      fontWeight: theme.typography.fontWeightBold,
      padding: theme.customStyles.groupedBodyRow.padding,
      textAlign: theme.customStyles.tdRightAlign.textAlign,
      '&:nth-child(2)': {
        // maxWidth: theme.customStyles.nthChild.maxWidth
        textAlign: theme.customStyles.tdLeftAlign.textAlign
      },
      cursor: 'pointer !important'
    }
  },
  negative: {
    color: theme.palette.error.main
  },
  manager: {
    color: theme.palette.accent.main,
    userSelect: 'none'
  },
  temf: {
    backgroundColor: theme.palette.grey.medium,
    '& td': {
      color: theme.palette.primary.main,
      fontWeight: theme.typography.fontWeightBold,
      fontSize: theme.customStyles.tableTemf.fontSize,
      padding: theme.customStyles.tableRow.padding,
      textAlign: theme.customStyles.tdRightAlign.textAlign,
      '&:nth-child(2)': {
        textAlign: theme.customStyles.tdLeftAlign.textAlign
      }
    }
  }
}));

const GroupedBody = ({ dataRows, groupRows, columns, sortBy, sortOrder, handleContextMenu }) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { getColumnSorting } = useSorting();

  const groupedRows = groupBy(dataRows, 'strategy');
  const groupKeys = Object.keys(groupedRows);
  const sortKey = sortBy || 'strategyRank';

  groupKeys.forEach(key => {
    groupedRows[key] = groupedRows[key].sort(getColumnSorting(sortKey, sortOrder));
  });

  const renderSingleRow = (groupKey, row) =>
    columns.map(column => {
      const { value, className } = getCellFormat(column.key, row[column.key]);
      const key = `${groupKey}-${column.key}-${row.strategyRank}}`;

      return (
        <TableCell key={key}>
          <TypographyWithTooltip
            variant="subtitle1"
            tooltipProps={{
              value,
              placement: 'right',
              TransitionComponent: Zoom,
              arrow: true
            }}
          >
            <span className={classes[className]}>{value}</span>
          </TypographyWithTooltip>
        </TableCell>
      );
    });

  const renderRowGroup = groupKey => {
    return groupedRows[groupKey].map(row => (
      <TableRow
        onClick={() => navigate(`/canned/${row.timeSeriesId}`)}
        onContextMenu={e => handleContextMenu(e, `/canned/${row.timeSeriesId}`)}
        key={row.rank}
        className={classes.linkRow}
      >
        {renderSingleRow(groupKey, row)}
      </TableRow>
    ));
  };

  const getClassName = value => {
    if (value < 0) {
      return classes.negative;
    }

    return null;
  };

  const renderGroupSummary = groupKey => {
    const groupSummary = groupRows.find(gr => gr.strategy === groupKey);
    const { strategy, value, allocation, monthToDateValue, yearToDateValue, attribute } = groupSummary;

    return (
      <TableRow className={classes.groupRow} 
      onClick={() => groupSummary.iD_TimeSeries && navigate(`/canned/${groupSummary.iD_TimeSeries}`)}>
        <TableCell />
        <TableCell>
          <TypographyWithTooltip
            variant="subtitle1"
            tooltipProps={{
              groupKey,
              placement: 'right',
              TransitionComponent: Zoom,
              arrow: true
            }}
          >
            <Label
              size="small"
              text={strategy}
              themeColor="primary"
              style={{ fontSize: '0.7rem', height: '20px', cursor: 'pointer' }}
            />
          </TypographyWithTooltip>
        </TableCell>
        <TableCell>
          <span>{numeral(value).format('$0,0')}</span>
        </TableCell>
        <TableCell>
          <span className={getClassName(allocation)}>{numeral(allocation).format('0.00%')}</span>
        </TableCell>
        <TableCell>
          <span className={getClassName(yearToDateValue)}>
            {numeral(yearToDateValue).format('0.00%')}
          </span>
        </TableCell>
        <TableCell>
          <span className={getClassName(monthToDateValue)}>
            {numeral(monthToDateValue).format('0.00%')}
          </span>
        </TableCell>
        <TableCell>
          <span className={getClassName(attribute)}>
            {numeral(attribute).format('0.00%')}
          </span>
        </TableCell>
        <TableCell />
      </TableRow>
    );
  };

  const renderTotalSummary = () => {
    const totalSummary = groupRows.find(gr => gr.strategy === 'Total');
    const { strategy, value, allocation, monthToDateValue, yearToDateValue, attribute } = totalSummary;

    return (
      <TableRow className={classes.temf}>
        <TableCell />
        <TableCell>{strategy}</TableCell>
        <TableCell>{numeral(value).format('$0,0')}</TableCell>
        <TableCell>{numeral(allocation).format('0.00%')}</TableCell>
        <TableCell>{numeral(yearToDateValue).format('0.00%')}</TableCell>
        <TableCell>{numeral(monthToDateValue).format('0.00%')}</TableCell>
        <TableCell>{numeral(attribute).format('0.00%')}</TableCell>
        <TableCell />
      </TableRow>
    );
  };

  return (
    <>
      <TableBody>
        {groupKeys.map(key => (
          <React.Fragment key={key}>
            {renderRowGroup(key)}
            {renderGroupSummary(key)}
          </React.Fragment>
        ))}
      </TableBody>
      <TableFooter>{renderTotalSummary()}</TableFooter>
    </>
  );
};

GroupedBody.propTypes = {
  dataRows: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  groupRows: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  columns: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  sortBy: PropTypes.string,
  sortOrder: PropTypes.oneOf(['asc', 'desc']),
  handleContextMenu: PropTypes.func.isRequired
};

GroupedBody.defaultProps = {
  sortBy: '',
  sortOrder: 'desc'
};

export default GroupedBody;
